import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../components/auth';
import { oauthApi } from './services/communicationsApi';

const OAUTH_RETURN_KEY = 'oauth-callback-return';

/**
 * Rendered when the browser lands on /callback after Google authorization.
 * Waits for an active CRM session, then POSTs to /api/communications/oauth/exchange.
 */
const OAuthCallbackPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const exchangeStarted = useRef(false);

  useEffect(() => {
    if (authLoading || exchangeStarted.current) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const googleError = params.get('error');

    if (googleError) {
      const reason = params.get('error_description') || googleError;
      window.location.replace(`/?view=communications&oauth=error&reason=${encodeURIComponent(reason)}`);
      return;
    }

    if (!code || !state) {
      setError('Missing authorization code. Visit Communications and click "Authorize with Google" to start.');
      return;
    }

    if (!user) {
      sessionStorage.setItem(OAUTH_RETURN_KEY, window.location.pathname + window.location.search);
      setError('Your CRM session is not active. Log in, then return to Communications and authorize again.');
      return;
    }

    const storageKey = `oauth-exchange:${code}`;
    if (sessionStorage.getItem(storageKey)) return;

    exchangeStarted.current = true;
    sessionStorage.setItem(storageKey, '1');

    oauthApi.exchangeCode(code, state)
      .then(({ channelId }) => {
        sessionStorage.removeItem(OAUTH_RETURN_KEY);
        window.location.replace(`/?view=communications&oauth=success&channelId=${channelId}`);
      })
      .catch(err => {
        sessionStorage.removeItem(storageKey);
        const msg = err?.response?.data?.error || err?.message || 'Token exchange failed';
        setError(msg);
        exchangeStarted.current = false;
      });
  }, [authLoading, user]);

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: '1rem',
        padding: '2rem',
        fontFamily: 'sans-serif',
        color: '#64748b',
        textAlign: 'center'
      }}>
        <p style={{ color: '#dc2626', margin: 0, maxWidth: 480 }}>{error}</p>
        <button
          type="button"
          onClick={() => window.location.replace('/?view=communications')}
          style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
        >
          Return to Communications
        </button>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: '1rem',
      fontFamily: 'sans-serif',
      color: '#64748b'
    }}>
      <div style={{
        width: 36,
        height: 36,
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ margin: 0, fontSize: '0.9rem' }}>
        {authLoading ? 'Verifying session…' : 'Completing authorization…'}
      </p>
    </div>
  );
};

export default OAuthCallbackPage;
